<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PaymentProvider } from '../services/payment-method.service'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  close: []
  select: [provider: PaymentProvider]
}>()

const { t } = useI18n()

interface Provider {
  id: PaymentProvider
  name: string
  descriptionKey: string
}

// TEMPORARILY DISABLED: PAYPAL, PRZELEWY24, KLARNA
const providerDefs: Provider[] = [
  {
    id: 'PAYU',
    name: 'PayU',
    descriptionKey: 'paymentProviderModal.payuDescription'
  },
  {
    id: 'STRIPE',
    name: 'Stripe',
    descriptionKey: 'paymentProviderModal.stripeDescription'
  }
  /* UNCOMMENT TO ENABLE OTHER PROVIDERS:
  ,
  {
    id: 'PAYPAL',
    name: 'PayPal',
    descriptionKey: 'paymentProviderModal.paypalDescription'
  },
  {
    id: 'PRZELEWY24',
    name: 'Przelewy24',
    descriptionKey: 'paymentProviderModal.przelewy24Description'
  },
  {
    id: 'KLARNA',
    name: 'Klarna',
    descriptionKey: 'paymentProviderModal.klarnaDescription'
  }
  */
]

const handleSelect = (provider: PaymentProvider) => {
  emit('select', provider)
}

const handleClose = () => {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
        @click.self="handleClose"
      >
        <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <!-- Header -->
          <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-900">{{ t('paymentProviderModal.title') }}</h2>
            <button
              @click="handleClose"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="p-6">
            <p class="text-sm text-gray-600 mb-6">
              {{ t('paymentProviderModal.subtitle') }}
            </p>

            <div class="space-y-3">
              <button
                v-for="provider in providerDefs"
                :key="provider.id"
                @click="handleSelect(provider.id)"
                class="w-full text-left border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all group"
              >
                <div class="flex items-start">
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-900 group-hover:text-blue-700 mb-1">
                      {{ provider.name }}
                    </h3>
                    <p class="text-sm text-gray-600">
                      {{ t(provider.descriptionKey) }}
                    </p>
                  </div>
                  <svg
                    class="w-6 h-6 text-gray-400 group-hover:text-blue-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          <!-- Footer -->
          <div class="border-t border-gray-200 px-6 py-4 flex justify-end">
            <button
              @click="handleClose"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}
</style>
